/**
 * The dashboard's light/dark setting, in the one place both sides can read it.
 *
 * This is a package rather than app code because three units need the same
 * answer: the dashboard client that offers the control, the site's root route
 * that stamps the class into the server-rendered `<html>`, and the API route
 * that writes the column. A copy in each is three chances to disagree about a
 * cookie name.
 *
 * The MySQL column on `users` is authoritative. The cookie exists only because
 * the column is read after the session resolves, and the `<html>` class has to
 * be decided before that — in the same response that renders the document.
 */

export const DASHBOARD_APPEARANCES = ["system", "light", "dark"] as const
export type DashboardAppearance = (typeof DASHBOARD_APPEARANCES)[number]

/** The look itself. "system" is a preference, never a rendered appearance. */
export type ResolvedDashboardAppearance = "light" | "dark"

export const DASHBOARD_APPEARANCE_COOKIE = "playbrain.dashboard.appearance"

/**
 * Dark, not system.
 *
 * Every existing account is looking at a dark console. Defaulting to "system"
 * would have moved most of them to light on the day this deployed, which is a
 * change nobody asked for.
 */
export const DEFAULT_DASHBOARD_APPEARANCE: DashboardAppearance = "dark"

export function isDashboardAppearance(value: unknown): value is DashboardAppearance {
  return typeof value === "string" && (DASHBOARD_APPEARANCES as readonly string[]).includes(value)
}

/** Anything unrecognised reads as dark, which is what the console has always been. */
export function dashboardAppearanceOrDefault(value: unknown): DashboardAppearance {
  return isDashboardAppearance(value) ? value : DEFAULT_DASHBOARD_APPEARANCE
}

/**
 * The class Radix's own token CSS keys off.
 *
 * `.light-theme` / `.dark-theme` rather than `.light` / `.dark`: Radix honours
 * both, and the longer names cannot collide with Tailwind's `dark` variant if
 * it is ever turned on. Because the class sits on `<html>`, every `<Theme>`
 * below can stay at its default `appearance="inherit"` and the look is settled
 * before React exists.
 */
export function dashboardAppearanceClass(appearance: ResolvedDashboardAppearance): string {
  return appearance === "dark" ? "dark-theme" : "light-theme"
}

/**
 * Not `HttpOnly`: the client writes this cookie too, when a "system" user's
 * device flips while they are looking at the page. It holds a theme name.
 *
 * A year, because it is a preference rather than a session fact, and `Lax` so
 * a normal navigation to the dashboard carries it.
 */
export function dashboardAppearanceCookie(appearance: DashboardAppearance): string {
  return `${DASHBOARD_APPEARANCE_COOKIE}=${appearance}; Path=/; Max-Age=31536000; SameSite=Lax`
}

/** Reads the preference out of a `Cookie` header, without pulling in a parser. */
export function readDashboardAppearanceCookie(cookieHeader: string | null | undefined): DashboardAppearance {
  if (!cookieHeader) return DEFAULT_DASHBOARD_APPEARANCE
  for (const part of cookieHeader.split(";")) {
    const [name, ...rest] = part.trim().split("=")
    if (name === DASHBOARD_APPEARANCE_COOKIE) {
      return dashboardAppearanceOrDefault(decodeURIComponent(rest.join("=")))
    }
  }
  return DEFAULT_DASHBOARD_APPEARANCE
}
